import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/base/Seo";
import { site } from "@/mocks/site";

const CONTACT_URL = "https://readdy.ai/api/form/da20lk3k8r5te5mqsd30";

const topics = ["我要投稿", "意见反馈", "版权合作", "商务合作", "其他事项"];

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "联系我们 - 吃瓜视频网",
  url: "https://bknos.cn/contact",
  description: "通过吃瓜视频网联系表单提交投稿、反馈、版权合作与商务合作意向。",
  contactPoint: {
    "@type": "ContactPoint",
    email: site.contact.email,
    telephone: site.contact.phone,
    contactType: "customer service",
  },
};

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("company_alt") as string)?.trim() ?? "";

    if (honeypot) {
      setStatus("success");
      setMsg("提交成功，我们已收到你的消息，会尽快与你联系！");
      form.reset();
      return;
    }
    fd.delete("company_alt");
    setSubmitting(true);
    setStatus("idle");
    try {
      const res = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const bodyMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setStatus("success");
        setMsg("提交成功，我们已收到你的消息，会尽快与你联系！");
        form.reset();
      } else {
        setStatus("error");
        setMsg(bodyMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMsg("网络异常，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="联系我们 - 投稿、反馈与版权合作"
        description="通过吃瓜视频网联系表单提交投稿、意见反馈、版权合作与商务合作意向，或通过邮件、微信等渠道与我们联系。"
        keywords="吃瓜视频网,联系我们,投稿,意见反馈,版权合作"
        canonical="https://bknos.cn/contact"
        type="contact"
        jsonLd={contactJsonLd}
      />

      <section className="bg-primary-600 py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">联系我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/90">
            有新鲜的瓜想分享、有宝贵的建议想提出，或有合作意向，欢迎通过以下方式与我们联系。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} data-readdy-form="chigua-contact" className="rounded-2xl bg-background-50 p-6 ring-1 ring-inset ring-background-200 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">填写联系表单</h2>
              <p className="mt-1 text-sm text-foreground-600">请尽量填写完整信息，以便我们更准确地回复你。</p>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如：小瓜"
                    className="w-full rounded-lg bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none ring-1 ring-inset ring-background-300 focus:ring-2 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-1.5 block text-sm font-medium text-foreground-800">电子邮箱</label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none ring-1 ring-inset ring-background-300 focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="c-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">事项类型</label>
                <select
                  id="c-topic"
                  name="topic"
                  defaultValue={topics[0]}
                  className="w-full rounded-lg bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none ring-1 ring-inset ring-background-300 focus:ring-2 focus:ring-primary-300"
                >
                  {topics.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="c-message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-800">
                  <span>留言内容</span>
                  <span className="text-xs font-normal text-foreground-400">最多 500 字</span>
                </label>
                <textarea
                  id="c-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  placeholder="请描述你的投稿内容、反馈建议或合作意向…"
                  className="w-full resize-none rounded-lg bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none ring-1 ring-inset ring-background-300 focus:ring-2 focus:ring-primary-300"
                ></textarea>
              </div>

              {/* 反垃圾蜜罐 */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
                >
                  <i className="ri-send-plane-line" />
                  {submitting ? "提交中…" : "提交留言"}
                </button>
                {status === "success" && (
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-secondary-600">
                    <i className="ri-checkbox-circle-line" /> {msg}
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-600">
                    <i className="ri-error-warning-line" /> {msg}
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Info */}
          <aside className="lg:col-span-5">
            <div className="rounded-2xl bg-background-100 p-6 ring-1 ring-inset ring-background-200 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">其他联系方式</h2>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50"><i className="ri-mail-line" /></span>
                  <div>
                    <p className="font-medium text-foreground-900">电子邮箱</p>
                    <p className="mt-0.5 text-foreground-600">{site.contact.email}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50"><i className="ri-wechat-line" /></span>
                  <div>
                    <p className="font-medium text-foreground-900">微信公众号</p>
                    <p className="mt-0.5 text-foreground-600">{site.contact.wechat}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50"><i className="ri-weibo-line" /></span>
                  <div>
                    <p className="font-medium text-foreground-900">官方微博</p>
                    <p className="mt-0.5 text-foreground-600">{site.contact.weibo}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-200 text-foreground-700"><i className="ri-phone-line" /></span>
                  <div>
                    <p className="font-medium text-foreground-900">客服电话</p>
                    <p className="mt-0.5 text-foreground-600">{site.contact.phone}（工作日 9:00-18:00）</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-6 rounded-2xl bg-accent-100 p-6 ring-1 ring-inset ring-accent-200">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-accent-900">
                <i className="ri-question-line" /> 常见问题
              </h3>
              <p className="mt-2 text-sm leading-6 text-accent-800">
                关于内容更新、观看方式、投稿规则等常见疑问，欢迎前往常见问题页面查看，大多问题都能在那里找到答案。
              </p>
              <Link to="/#faq" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent-800 underline-offset-2 hover:underline">
                查看常见问题 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}